import { ChevronDown } from 'lucide-react';
import { getFileIcon } from '../../utils/fileUtils';
import type {
  ClassificationLevelDetail,
  ConfidenceLabel,
  FunctionClassificationEntry,
  RecordClassificationEntry,
} from '../../utils/monitoringClassification';
import { formatConfidenceLabel, formatScoreAsPercentage } from '../../utils/monitoringClassification';
import ClassificationLevelBlock from './ClassificationLevelBlock';
import FunctionClassificationLevelBlock from './FunctionClassificationLevelBlock';

export type ClassificationStatus = 'classified' | 'unclassified';

export interface MonitoringTableRow {
  id: string;
  title: string;
  contentType: string;
  pnccProcessingId?: string;
  classificationStatus: ClassificationStatus;
  rankingScore?: number | null;
  confidenceLabel?: ConfidenceLabel;
  classificationEntries?: RecordClassificationEntry[];
  functionClassificationEntries?: FunctionClassificationEntry[];
  userOverridden?: boolean;
  overrideLabel?: string;
  finalRecordLevels?: [
    ClassificationLevelDetail,
    ClassificationLevelDetail,
    ClassificationLevelDetail,
  ];
}

function ConfidenceBadge({ label }: { label: ConfidenceLabel }) {
  const isHigh = label === 'high_confidence';
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
        isHigh ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
      }`}
    >
      {formatConfidenceLabel(label)}
    </span>
  );
}

function CellDropdown({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <details
      className="group relative min-w-[180px] max-w-[280px]"
      onClick={(e) => e.stopPropagation()}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:border-emerald-200 hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 truncate">{label}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50/80 p-3">
        {children}
      </div>
    </details>
  );
}

function FinalClassificationContent({
  levels,
  classificationKind = 'record',
}: {
  levels: [
    ClassificationLevelDetail,
    ClassificationLevelDetail,
    ClassificationLevelDetail,
  ];
  classificationKind?: 'record' | 'function';
}) {
  const labels =
    classificationKind === 'function'
      ? (['Domain', 'Subdomain', 'Function'] as const)
      : (['Level 1', 'Level 2', 'Level 3'] as const);

  return (
    <div className="min-w-[160px] space-y-1.5 text-xs text-gray-700">
      {labels.map((label, index) => {
        const level = levels[index];
        if (classificationKind === 'function') {
          const displayName = level.name !== '—' ? level.name : level.code;
          return (
            <p key={label}>
              <span className="font-medium text-gray-500">{label}: </span>
              <span className="text-gray-700">{displayName}</span>
            </p>
          );
        }

        return (
          <p key={label}>
            <span className="font-medium text-gray-500">{label}: </span>
            <span className="font-mono">{level.code}</span>
            {level.name !== '—' && <span className="text-gray-600"> — {level.name}</span>}
          </p>
        );
      })}
    </div>
  );
}

function ClassificationStatusBadge({ label, variant }: { label: string; variant: 'system' | 'override' }) {
  return (
    <span
      className={`inline-flex max-w-[220px] rounded-full px-2.5 py-1 text-xs font-semibold ${
        variant === 'override'
          ? 'bg-violet-100 text-violet-800'
          : 'bg-slate-100 text-slate-700'
      }`}
    >
      {label}
    </span>
  );
}

interface MonitoringClassificationTableProps {
  records: MonitoringTableRow[];
  showFinalClassification?: boolean;
  showClassificationStatus?: boolean;
  showSystemSelectedStatus?: boolean;
  classificationKind?: 'record' | 'function';
}

export default function MonitoringClassificationTable({
  records,
  showFinalClassification = false,
  showClassificationStatus = true,
  showSystemSelectedStatus = false,
  classificationKind = 'record',
}: MonitoringClassificationTableProps) {
  const potentialClassificationsLabel =
    classificationKind === 'function'
      ? 'Potential function classifications'
      : 'Potential record classifications';

  return (
    <div className="max-w-full overflow-x-auto overscroll-x-contain">
      <table className="w-full min-w-0 border-collapse text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="w-12 px-3 py-3 text-center">#</th>
            <th className="min-w-[140px] px-4 py-3">Record</th>
            <th className="min-w-[120px] px-4 py-3">Confidence</th>
            <th className="min-w-[110px] px-4 py-3">Ranking score</th>
            {showClassificationStatus && (
              <th className="min-w-[140px] px-4 py-3">Classification Status</th>
            )}
            {showFinalClassification && (
              <th className="min-w-[160px] px-4 py-3">Final classification</th>
            )}
            <th className="min-w-[160px] px-4 py-3">{potentialClassificationsLabel}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {records.map((record, index) => {
            const isClassified = record.classificationStatus === 'classified';
            const isHighConfidence = record.confidenceLabel === 'high_confidence';
            const candidateCount =
              classificationKind === 'function'
                ? (record.functionClassificationEntries?.length ?? 0)
                : (record.classificationEntries?.length ?? 0);

            return (
              <tr key={record.id} className="align-top hover:bg-gray-50/80">
                <td className="px-3 py-3 text-center text-xs font-semibold tabular-nums text-gray-500">
                  {index + 1}
                </td>
                <td className="px-4 py-3">
                  <div className="flex min-w-0 max-w-[220px] items-start gap-2">
                    <span className="mt-0.5 shrink-0">{getFileIcon(record.contentType, 'w-4 h-4')}</span>
                    <a
                      href={`/records/${record.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 text-left font-medium text-gray-900 hover:text-emerald-800 hover:underline break-words"
                    >
                      {record.title}
                    </a>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {isClassified && record.confidenceLabel ? (
                    <ConfidenceBadge label={record.confidenceLabel} />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {isClassified ? (
                    <span
                      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
                        isHighConfidence
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {formatScoreAsPercentage(record.rankingScore ?? null)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                {showClassificationStatus && (
                  <td className="px-4 py-3">
                    {record.overrideLabel ? (
                      <ClassificationStatusBadge label={record.overrideLabel} variant="override" />
                    ) : showSystemSelectedStatus && isClassified ? (
                      <ClassificationStatusBadge label="System Selected" variant="system" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                )}
                {showFinalClassification && (
                  <td className="px-4 py-3">
                    {record.finalRecordLevels ? (
                      <FinalClassificationContent
                        levels={record.finalRecordLevels}
                        classificationKind={classificationKind}
                      />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3">
                  {isClassified && candidateCount > 0 ? (
                    <CellDropdown
                      label={`${candidateCount} candidate${candidateCount !== 1 ? 's' : ''}`}
                    >
                      {classificationKind === 'function' ? (
                        <FunctionClassificationLevelBlock
                          entries={record.functionClassificationEntries!}
                        />
                      ) : (
                        <ClassificationLevelBlock entries={record.classificationEntries!} />
                      )}
                    </CellDropdown>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
